import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { isChatSourced } from "@/common/helpers/transcript";
import { useContext } from "@/store/context";
// The BE can return a chunk whose `word` spans multiple whitespace-separated
// tokens when timing causes adjacent words to coalesce. The FE renders one
// <span> per chunk and tracks edits in char-space, so a multi-word chunk
// would bleed session-green onto un-edited neighbours. Splitting at ingest
// restores the "one chunk = one rendered word" invariant the rest of the FE
// silently assumes. Timing is split proportionally to each part's char count.
const splitChunkOnWhitespace = (chunk) => {
    const parts = chunk.word.trim().split(/\s+/).filter(Boolean);
    // The BE bakes a leading space into every word; char-range math reads `word` while the DOM renders it trimmed
    if (parts.length <= 1) {
        const word = parts[0] ?? chunk.word;
        return word === chunk.word ? [chunk] : [{ ...chunk, word }];
    }
    const span = chunk.end - chunk.start;
    const totalChars = parts.reduce((sum, part) => sum + part.length, 0);
    let consumed = 0;
    return parts.map((word) => {
        const startFrac = totalChars === 0 ? 0 : consumed / totalChars;
        consumed += word.length;
        const endFrac = totalChars === 0 ? 1 : consumed / totalChars;
        return {
            word,
            start: chunk.start + span * startFrac,
            end: chunk.start + span * endFrac,
            speaker: chunk.speaker,
            // Only chunks without an `index` are split (see normalizeSegmentWords), but a partial
            // payload can still carry the edit coordinates, so pass them through.
            index: chunk.index,
            groupindex: chunk.groupindex,
            wordindex: chunk.wordindex
        };
    });
};
// A chunk carrying `index` is one server `<t>`, which the server excerpts as a single Excerptable
// fragment. Splitting it would give every part the same rc-e-i token index, so a selection names
// more fragments than it covers and the excerpt runs past it. This subsumes the chat/backroom case:
// those rows are emitted as a single `index: 0` chunk. Only producers with no fragment grid of
// their own (external content, keyed off the rendered word position) still split.
const normalizeSegmentWords = (segment) => {
    let changed = false;
    const words = [];
    for (const chunk of segment.words) {
        const split = chunk.index === undefined ? splitChunkOnWhitespace(chunk) : [chunk];
        if (split.length !== 1 || split[0] !== chunk)
            changed = true;
        words.push(...split);
    }
    return changed ? { ...segment, words } : segment;
};
const normalizeTranscriptData = (data) => {
    let changed = false;
    const blocks = data.blocks.map((block) => {
        const segments = block.segments.map((segment) => {
            const normalized = normalizeSegmentWords(segment);
            if (normalized !== segment)
                changed = true;
            return normalized;
        });
        return changed ? { ...block, segments } : block;
    });
    return changed ? { ...data, blocks } : data;
};
// Compares only fields that drive UI behaviour. Adapter-specific timestamps or
// counts never invalidate the ref on their own.
const engineStatesEqual = (a, b) => {
    if (a.size !== b.size)
        return false;
    for (const [engine, x] of a) {
        const y = b.get(engine);
        if (!y)
            return false;
        if (x.isPrimary !== y.isPrimary ||
            x.isRegenerating !== y.isRegenerating ||
            x.isAnalyzing !== y.isAnalyzing ||
            x.failureReason !== y.failureReason) {
            return false;
        }
    }
    return true;
};
const buildLatestByEngine = (states) => {
    const latest = new Map();
    for (const s of states) {
        // Last-write-wins within a single call. Adapters that need chronological
        // dedup do that upstream before handing the array in.
        latest.set(s.engineId, s);
    }
    return latest;
};
const isEngineInProgress = (s) => (s.isRegenerating || s.isAnalyzing) && !s.failureReason;
export const useTranscriptionsStore = defineStore("transcriptions", () => {
    const { currentStudyId } = storeToRefs(useContext());
    // Cancellation token: `init()` bumps it; adapters snapshot it before their
    // first await and drop post-await writes if the value changed.
    const contextGeneration = ref(0);
    const transcriptionContext = ref(null);
    // Keyed by engine ID. Adapters own the source-specific status mapping.
    const engineStates = ref(new Map());
    const selectedEngine = ref(null);
    // Cache slots keyed by engine ID, or `null` for an engine-agnostic primary
    // slot used before engine state has hydrated. `selectedEngine === null`
    // naturally points the consumer-facing computeds at this slot.
    const transcriptions = ref(new Map());
    // Slots the FE is currently GET-ing an existing transcript for.
    const fetchingTranscriptions = ref(new Set());
    // Slots the BE is (re)producing a transcript for (via transcribe()).
    const generatingTranscriptions = ref(new Set());
    // True from init() until the first fetch resolves — covers the pre-hydration
    // window where selectedEngine is still null, which a slot-keyed Set cannot.
    const isInitialTranscriptLoad = ref(false);
    const transcriptionErrorMap = ref(new Map());
    // Plain ref (not a computed over an external store) so adapters with their
    // own catalogs can publish engines without a cross-store dependency.
    const availableEngines = ref({});
    const transcription = computed(() => transcriptions.value.get(selectedEngine.value));
    const transcriptionError = computed(() => transcriptionErrorMap.value.get(selectedEngine.value)?.error);
    const transcriptionErrorCode = computed(() => transcriptionErrorMap.value.get(selectedEngine.value)?.code);
    const primaryEngine = computed(() => {
        for (const s of engineStates.value.values()) {
            if (s.isPrimary)
                return s.engineId;
        }
        return null;
    });
    // Hydrate from the primary on first arrival; the guard preserves any
    // explicit `selectEngine` choice across subsequent mutations.
    const hydrateSelectedEngine = () => {
        if (selectedEngine.value == null && primaryEngine.value !== null) {
            selectedEngine.value = primaryEngine.value;
        }
    };
    const requireContext = () => {
        if (!transcriptionContext.value || currentStudyId.value === undefined) {
            throw new Error("[useTranscriptionsStore] init({activityId, contentId}) must be called before context-dependent actions");
        }
        return { ...transcriptionContext.value, studyId: currentStudyId.value };
    };
    const init = (ctx) => {
        // Bump before resetting so any in-flight adapter request resuming after
        // this point sees a stale generation and drops its writes.
        contextGeneration.value++;
        engineStates.value = new Map();
        selectedEngine.value = null;
        transcriptions.value = new Map();
        fetchingTranscriptions.value = new Set();
        generatingTranscriptions.value = new Set();
        isInitialTranscriptLoad.value = true;
        transcriptionErrorMap.value = new Map();
        transcriptionContext.value = { ...ctx };
    };
    // Reassigning the Map ref keeps identity-tracking watchers able to diff
    // `prev` vs `next` instances.
    const applyEngineState = (state) => {
        const next = new Map(engineStates.value);
        next.set(state.engineId, state);
        engineStates.value = next;
        hydrateSelectedEngine();
    };
    // Short-circuits on a structurally equal payload so downstream computeds
    // and watchers skip a recompute on no-op writes.
    const setEngineStates = (states) => {
        const deduped = buildLatestByEngine(states);
        if (engineStatesEqual(engineStates.value, deduped))
            return;
        engineStates.value = deduped;
        hydrateSelectedEngine();
    };
    const setAvailableEngines = (catalog) => {
        availableEngines.value = { ...catalog };
    };
    const getEngineDisplayName = (engine) => {
        return availableEngines.value[engine] ?? String(engine);
    };
    // Keyed by source too: a spoken edit must not overwrite the chat row sharing its id.
    const segmentKey = (segment) => `${isChatSourced(segment) ? "c" : "s"}:${segment.id}`;
    // Substitute segments by id, mutating `TranscriptData` in place so the
    // `transcription` watcher doesn't fire and re-seed an initial-load shimmer.
    // Stale ids are a silent no-op (the BE 422 would have surfaced first).
    // `slot` lets callers pin a stable target (e.g. the primary slot for
    // primary-only edits) so an in-flight engine switch can't misroute the write.
    const replaceSegments = (segments, slot = selectedEngine.value) => {
        const data = transcriptions.value.get(slot);
        if (!data)
            return;
        const byKey = new Map(segments.map((s) => [segmentKey(s), normalizeSegmentWords(s)]));
        for (const block of data.blocks) {
            for (let i = 0; i < block.segments.length; i++) {
                const key = segmentKey(block.segments[i]);
                const replacement = byKey.get(key);
                if (!replacement)
                    continue;
                block.segments[i] = replacement;
                byKey.delete(key);
                if (byKey.size === 0)
                    return;
            }
        }
    };
    // Chat and spoken rows can share an id, so the id alone names two segments.
    const findSegment = (id, slot, chatSourced) => {
        const data = transcriptions.value.get(slot);
        if (!data)
            return undefined;
        for (const block of data.blocks) {
            for (const seg of block.segments) {
                if (seg.id === id && isChatSourced(seg) === chatSourced)
                    return seg;
            }
        }
        return undefined;
    };
    const getSegmentText = (id, slot = selectedEngine.value, chatSourced = false) => findSegment(id, slot, chatSourced)?.text;
    const getSegmentWords = (id, slot = selectedEngine.value, chatSourced = false) => findSegment(id, slot, chatSourced)?.words.map((w) => w.word);
    // Returns the live segment object so callers can snapshot it (for optimistic-
    // edit rollback) or read its timed words. `replaceSegments` swaps the object
    // by id rather than mutating it, so a captured reference stays valid as a
    // pre-edit snapshot after later optimistic writes.
    const getSegment = (id, slot = selectedEngine.value, chatSourced = false) => findSegment(id, slot, chatSourced);
    const selectEngine = (engine) => {
        selectedEngine.value = engine;
    };
    const setTranscription = (slot, data) => {
        transcriptions.value.set(slot, normalizeTranscriptData(data));
    };
    const setFetching = (slot) => {
        fetchingTranscriptions.value.add(slot);
    };
    const clearFetching = (slot) => {
        fetchingTranscriptions.value.delete(slot);
    };
    // First fetch resolution ends the initial-load window, whatever its slot.
    const resolveInitialTranscriptLoad = () => {
        isInitialTranscriptLoad.value = false;
    };
    const setGenerating = (slot) => {
        generatingTranscriptions.value.add(slot);
    };
    const clearGenerating = (slot) => {
        generatingTranscriptions.value.delete(slot);
    };
    const setError = (slot, err) => {
        transcriptionErrorMap.value.set(slot, err);
    };
    const clearError = (slot) => {
        transcriptionErrorMap.value.delete(slot);
    };
    const selectedEngineState = computed(() => selectedEngine.value !== null ? engineStates.value.get(selectedEngine.value) : undefined);
    const selectedEngineDisplayName = computed(() => selectedEngine.value !== null ? getEngineDisplayName(selectedEngine.value) : "");
    const allEnginesComplete = computed(() => {
        for (const s of engineStates.value.values()) {
            if (isEngineInProgress(s))
                return false;
        }
        return true;
    });
    const isSelectedEngineRetranscribing = computed(() => {
        const state = selectedEngineState.value;
        if (!state || state.failureReason)
            return false;
        return state.isRegenerating;
    });
    const isSelectedEngineAnalyzing = computed(() => {
        const state = selectedEngineState.value;
        if (!state || state.failureReason)
            return false;
        return state.isAnalyzing;
    });
    // Terminal failure on the current slot — a re-transcribe target, not a transcript to show.
    const isSelectedEngineFailed = computed(() => !!selectedEngineState.value?.failureReason);
    // BE is producing the current transcript. Reads the current slot
    // (selectedEngine, null in restricted mode); retranscribe counts too.
    const isGeneratingTranscript = computed(() => isSelectedEngineRetranscribing.value || generatingTranscriptions.value.has(selectedEngine.value));
    // FE is retrieving the current transcript — the initial load (any slot) or a
    // later GET of the current slot. Distinct from generation.
    const isFetchingTranscript = computed(() => isInitialTranscriptLoad.value || fetchingTranscriptions.value.has(selectedEngine.value));
    return {
        availableEngines,
        engineStates,
        primaryEngine,
        selectedEngine,
        selectedEngineDisplayName,
        transcriptions,
        transcription,
        fetchingTranscriptions,
        generatingTranscriptions,
        isInitialTranscriptLoad,
        transcriptionContext,
        transcriptionErrorMap,
        transcriptionError,
        transcriptionErrorCode,
        selectedEngineState,
        allEnginesComplete,
        isSelectedEngineRetranscribing,
        isSelectedEngineAnalyzing,
        isSelectedEngineFailed,
        isGeneratingTranscript,
        isFetchingTranscript,
        contextGeneration,
        init,
        applyEngineState,
        setEngineStates,
        selectEngine,
        replaceSegments,
        getSegmentText,
        getSegmentWords,
        getSegment,
        setAvailableEngines,
        setTranscription,
        setFetching,
        clearFetching,
        resolveInitialTranscriptLoad,
        setGenerating,
        clearGenerating,
        setError,
        clearError,
        getEngineDisplayName,
        requireContext
    };
});
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTranscriptionsStore, import.meta.hot));
}
